import { Shield, Link2, UserCheck, Cog, RefreshCw, AlertTriangle } from 'lucide-react'
import { Badge } from './ui/badge'
import { cn } from '@/lib/utils'
import type { EnterpriseStatus } from '@/types/orchestrator'

interface EnterpriseStatusBannerProps {
  status: EnterpriseStatus | null
  isFallbackMode?: boolean
  fallbackReason?: string | null
  className?: string
}

/**
 * Banner showing enterprise feature availability
 * Displays status of Orchestrator, Policy Engine, HITL, etc.
 */
export function EnterpriseStatusBanner({
  status,
  isFallbackMode = false,
  fallbackReason,
  className,
}: EnterpriseStatusBannerProps) {
  // Show fallback warning if in fallback mode
  if (isFallbackMode) {
    return (
      <div
        className={cn(
          'flex items-center gap-3 px-4 py-2 rounded-lg',
          'bg-warning/10 border border-warning/30',
          className
        )}
        role="alert"
      >
        <AlertTriangle className="h-4 w-4 text-warning flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-warning">
            Orchestrator Unavailable
          </span>
          {fallbackReason && (
            <span className="text-xs text-warning/80 ml-2">
              ({fallbackReason})
            </span>
          )}
        </div>
        <Badge variant="outline" className="text-xs border-warning/50 text-warning">
          Fallback Mode
        </Badge>
      </div>
    )
  }

  // Don't show banner if no status
  if (!status) {
    return null
  }

  const features = [
    {
      key: 'orchestrator',
      label: 'Orchestrator',
      icon: Link2,
      enabled: status.orchestrator_connected,
    },
    {
      key: 'policy',
      label: 'Policy Engine',
      icon: Shield,
      enabled: status.policy_engine_enabled,
    },
    {
      key: 'hitl',
      label: 'HITL',
      icon: UserCheck,
      enabled: status.hitl_enabled,
    },
    {
      key: 'executor',
      label: 'Executor',
      icon: Cog,
      enabled: status.executor_framework_enabled,
    },
    {
      key: 'recovery',
      label: 'Error Recovery',
      icon: RefreshCw,
      enabled: status.error_recovery_enabled,
    },
  ]

  const enabledCount = features.filter((f) => f.enabled).length
  const allEnabled = enabledCount === features.length

  return (
    <div
      className={cn(
        'flex items-center gap-4 px-4 py-2 rounded-lg',
        allEnabled
          ? 'bg-success/10 border border-success/30'
          : 'bg-muted/50 border border-border',
        className
      )}
      role="status"
      aria-label="Enterprise features status"
    >
      <span className="text-xs font-medium text-muted-foreground">Enterprise:</span>
      <div className="flex items-center gap-3 flex-wrap">
        {features.map(({ key, label, icon: Icon, enabled }) => (
          <div
            key={key}
            className={cn(
              'flex items-center gap-1.5 text-xs',
              enabled
                ? 'text-success'
                : 'text-muted-foreground/60'
            )}
            title={`${label}: ${enabled ? 'Enabled' : 'Disabled'}`}
          >
            <Icon className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{label}</span>
            <span
              className={cn(
                'w-1.5 h-1.5 rounded-full',
                enabled ? 'bg-success' : 'bg-muted-foreground/30'
              )}
            />
          </div>
        ))}
      </div>
    </div>
  )
}
